import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SplitBillModal = ({
    isOpen,
    onClose,
    originalBillNo,
    tableno,
    areaId,
    tableType,
    previousOrders,
    userType,
    apiBaseUrl,
    logginedDB,
    genSettings,
    taxGroups,
    onSuccess
}) => {
    const [splitMode, setSplitMode] = useState('items'); // 'items' or 'equal'
    const [numEqualSplits, setNumEqualSplits] = useState(2);
    
    // State for equal splits payment allocation
    const [equalSplits, setEqualSplits] = useState([]);

    // State for itemized splitting
    const [splitBills, setSplitBills] = useState([
        { id: 1, items: [], discount: 0, otherCharges: 0, paymentMethod: 'cash', customerName: '', paymentDetails: { cash: 0, card: 0, online: 0, credit: 0 } },
        { id: 2, items: [], discount: 0, otherCharges: 0, paymentMethod: 'cash', customerName: '', paymentDetails: { cash: 0, card: 0, online: 0, credit: 0 } }
    ]);
    const [unallocatedItems, setUnallocatedItems] = useState([]);

    // Calculate total original bill amount
    const originalTotalAmt = previousOrders.reduce((sum, item) => sum + (item.Qty * item.Rate), 0);

    // Initialize item lists
    useEffect(() => {
        if (isOpen && previousOrders) {
            // Setup unallocated items with their initial quantities
            setUnallocatedItems(
                previousOrders.map(item => ({
                    ...item,
                    unallocatedQty: item.Qty
                }))
            );
            // Reset split bills items
            setSplitBills([
                { id: 1, items: [], discount: 0, otherCharges: 0, paymentMethod: 'cash', customerName: '', paymentDetails: { cash: 0, card: 0, online: 0, credit: 0 } },
                { id: 2, items: [], discount: 0, otherCharges: 0, paymentMethod: 'cash', customerName: '', paymentDetails: { cash: 0, card: 0, online: 0, credit: 0 } }
            ]);
        }
    }, [isOpen, previousOrders]);

    // Setup equal splits array when count changes
    useEffect(() => {
        const defaultSplits = Array.from({ length: numEqualSplits }, (_, i) => ({
            id: i + 1,
            customerName: `Guest ${i + 1}`,
            paymentMethod: 'cash',
            paymentDetails: { cash: 0, card: 0, online: 0, credit: 0 }
        }));
        setEqualSplits(defaultSplits);
    }, [numEqualSplits]);

    if (!isOpen) return null;

    const calculateTax = (amount) => {
        if (!genSettings || genSettings.Tax_Mode === 'NONE') {
            return { cgst: 0, sgst: 0, totalTax: 0 };
        }

        const defaultTax = (taxGroups || []).find(
            t => String(t.id_tax) === String(genSettings.Default_Tax_Id)
        );
        const taxPercent = parseFloat(defaultTax?.gstPercent) || 0;
        const baseAmount = parseFloat(amount) || 0;
        let totalTax = 0;

        if (taxPercent > 0 && baseAmount > 0) {
            if (genSettings.isTaxInclusive === 1) {
                totalTax = baseAmount - (baseAmount / (1 + (taxPercent / 100)));
            } else {
                totalTax = baseAmount * (taxPercent / 100);
            }
        }

        return {
            cgst: totalTax / 2,
            sgst: totalTax / 2,
            totalTax
        };
    };

    // Helper to calculate totals based on settings
    const calculateTotals = (items, discountPercent = 0, otherCharges = 0) => {
        const itemsTotal = items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
        let combinedTotal = itemsTotal;
        const tax = calculateTax(itemsTotal);

        // Apply Tax if NOT inclusive
        if (genSettings && genSettings.isTaxInclusive === 0 && genSettings.Tax_Mode !== 'NONE') {
            combinedTotal += tax.totalTax;
        }

        // Apply discount
        const discountAmt = combinedTotal * (discountPercent / 100);
        combinedTotal -= discountAmt;

        // Add other charges
        combinedTotal += parseFloat(otherCharges) || 0;

        // Rounding
        let finalAmount = combinedTotal;
        let roundOff = 0;
        const roundingMode = genSettings?.Rounding_Mode || 'NONE';
        if (roundingMode === 'UP') {
            finalAmount = Math.ceil(combinedTotal);
            roundOff = finalAmount - combinedTotal;
        } else if (roundingMode === 'DOWN') {
            finalAmount = Math.floor(combinedTotal);
            roundOff = finalAmount - combinedTotal;
        } else if (roundingMode === 'HALF') {
            finalAmount = Math.round(combinedTotal);
            roundOff = finalAmount - combinedTotal;
        }

        return {
            totalAmount: itemsTotal.toFixed(2),
            discount: discountAmt.toFixed(2),
            netAmount: finalAmount.toFixed(2),
            roundOff: roundOff.toFixed(2),
            cgst: tax.cgst.toFixed(2),
            sgst: tax.sgst.toFixed(2),
            totalTax: tax.totalTax.toFixed(2)
        };
    };

    // Equal Split Totals Helper
    const calculateEqualSplitTotals = () => {
        let combinedTotal = originalTotalAmt;
        const originalTax = calculateTax(originalTotalAmt);

        // Apply Tax if NOT inclusive
        if (genSettings && genSettings.isTaxInclusive === 0 && genSettings.Tax_Mode !== 'NONE') {
            combinedTotal += originalTax.totalTax;
        }

        // Total bill amount after tax
        const splitAmount = combinedTotal / numEqualSplits;
        
        // Round each split part individually
        let finalAmount = splitAmount;
        let roundOff = 0;
        const roundingMode = genSettings?.Rounding_Mode || 'NONE';
        if (roundingMode === 'UP') {
            finalAmount = Math.ceil(splitAmount);
            roundOff = finalAmount - splitAmount;
        } else if (roundingMode === 'DOWN') {
            finalAmount = Math.floor(splitAmount);
            roundOff = finalAmount - splitAmount;
        } else if (roundingMode === 'HALF') {
            finalAmount = Math.round(splitAmount);
            roundOff = finalAmount - splitAmount;
        }

        return {
            shareItemsTotal: (originalTotalAmt / numEqualSplits).toFixed(2),
            shareNetAmount: finalAmount.toFixed(2),
            shareRoundOff: roundOff.toFixed(2),
            shareCgst: (originalTax.cgst / numEqualSplits).toFixed(2),
            shareSgst: (originalTax.sgst / numEqualSplits).toFixed(2),
            shareTotalTax: (originalTax.totalTax / numEqualSplits).toFixed(2)
        };
    };

    // Move item to a split bill
    const moveItemToBill = (itemIndex, billId) => {
        const item = unallocatedItems[itemIndex];
        if (item.unallocatedQty <= 0) return;

        // Decrease unallocated qty
        const updatedUnallocated = [...unallocatedItems];
        updatedUnallocated[itemIndex].unallocatedQty -= 1;
        setUnallocatedItems(updatedUnallocated);

        // Increase/add in selected split bill
        const updatedBills = splitBills.map(bill => {
            if (bill.id === billId) {
                const existingItemIdx = bill.items.findIndex(
                    i => i.Itemid === item.Itemid && i.variantId === item.variantId && i.addonId === item.addonId
                );

                if (existingItemIdx > -1) {
                    const newItems = [...bill.items];
                    newItems[existingItemIdx].quantity += 1;
                    return { ...bill, items: newItems };
                } else {
                    return {
                        ...bill,
                        items: [
                            ...bill.items,
                            {
                                Itemid: item.Itemid,
                                ItemName: item.ItemName,
                                rate: item.Rate,
                                quantity: 1,
                                variantId: item.variantId,
                                VariantName: item.VariantName,
                                addonId: item.addonId,
                                addonName: item.addonName
                            }
                        ]
                    };
                }
            }
            return bill;
        });
        setSplitBills(updatedBills);
    };

    // Remove item from a split bill (move back to unallocated)
    const removeItemFromBill = (billId, itemKey) => {
        const bill = splitBills.find(b => b.id === billId);
        const item = bill.items[itemKey];

        // Decrease bill item qty
        const updatedBills = splitBills.map(b => {
            if (b.id === billId) {
                const newItems = [...b.items];
                newItems[itemKey].quantity -= 1;
                return {
                    ...b,
                    items: newItems.filter(i => i.quantity > 0)
                };
            }
            return b;
        });
        setSplitBills(updatedBills);

        // Increase unallocated item qty
        const updatedUnallocated = unallocatedItems.map(i => {
            if (i.Itemid === item.Itemid && i.variantId === item.variantId && i.addonId === item.addonId) {
                return { ...i, unallocatedQty: i.unallocatedQty + 1 };
            }
            return i;
        });
        setUnallocatedItems(updatedUnallocated);
    };

    // Add another split bill card
    const addSplitBill = () => {
        setSplitBills([
            ...splitBills,
            {
                id: splitBills.length + 1,
                items: [],
                discount: 0,
                otherCharges: 0,
                paymentMethod: 'cash',
                customerName: '',
                paymentDetails: { cash: 0, card: 0, online: 0, credit: 0 }
            }
        ]);
    };

    // Remove a split bill card (must return its items to unallocated)
    const removeSplitBill = (billId) => {
        if (splitBills.length <= 2) return;

        const billToRemove = splitBills.find(b => b.id === billId);
        
        // Return items to unallocated pool
        const updatedUnallocated = unallocatedItems.map(unItem => {
            const billItem = billToRemove.items.find(
                bi => bi.Itemid === unItem.Itemid && bi.variantId === unItem.variantId && bi.addonId === unItem.addonId
            );
            if (billItem) {
                return { ...unItem, unallocatedQty: unItem.unallocatedQty + billItem.quantity };
            }
            return unItem;
        });

        setUnallocatedItems(updatedUnallocated);
        setSplitBills(splitBills.filter(b => b.id !== billId).map((b, idx) => ({ ...b, id: idx + 1 })));
    };

    // Handle Equal Split Submit
    const handleSettleEqualSplit = async () => {
        try {
            const shareTotals = calculateEqualSplitTotals();
            const payloadSplits = equalSplits.map(eq => {
                const splitItems = previousOrders.map(item => ({
                    productId: item.Itemid,
                    productName: item.ItemName,
                    rate: item.Rate,
                    quantity: item.Qty / numEqualSplits,
                    variantId: item.variantId,
                    variantName: item.VariantName,
                    addonId: item.addonId,
                    addonName: item.addonName
                }));

                const details = { ...eq.paymentDetails };
                details[eq.paymentMethod] = parseFloat(shareTotals.shareNetAmount);

                return {
                    customerName: eq.customerName,
                    items: splitItems,
                    totalAmount: shareTotals.shareItemsTotal,
                    discount: 0,
                    otherCharges: 0,
                    netAmount: shareTotals.shareNetAmount,
                    roundOff: shareTotals.shareRoundOff,
                    cgst: shareTotals.shareCgst,
                    sgst: shareTotals.shareSgst,
                    totalTax: shareTotals.shareTotalTax,
                    paymentDetails: details,
                    creditAccount: eq.paymentMethod === 'due' ? 'Customer Credit' : ' '
                };
            });

            const res = await axios.post(`${apiBaseUrl}/splitBill/${logginedDB}`, {
                originalBillNo,
                tableno,
                area_id: areaId,
                tableType,
                userTypeInt: userType,
                splits: payloadSplits
            });

            if (res.data.success) {
                alert("Bills split and settled successfully!");
                onSuccess(res.data.billNos);
                onClose();
            } else {
                alert("Failed to settle split bill: " + res.data.message);
            }
        } catch (error) {
            console.error(error);
            alert("Error settling split bill.");
        }
    };

    // Handle Itemized Split Submit
    const handleSettleItemizedSplit = async () => {
        // Validate if all items are fully allocated
        const hasUnallocated = unallocatedItems.some(i => i.unallocatedQty > 0);
        if (hasUnallocated) {
            alert("Please allocate all items before finalizing the split bill.");
            return;
        }

        // Validate that each split bill has at least one item
        const emptyBill = splitBills.some(b => b.items.length === 0);
        if (emptyBill) {
            alert("Each split bill must contain at least one item.");
            return;
        }

        try {
            const payloadSplits = splitBills.map(b => {
                const totals = calculateTotals(b.items, b.discount, b.otherCharges);
                const details = { ...b.paymentDetails };
                details[b.paymentMethod] = parseFloat(totals.netAmount);

                return {
                    customerName: b.customerName || `Guest ${b.id}`,
                    items: b.items.map(i => ({
                        productId: i.Itemid,
                        productName: i.ItemName,
                        rate: i.rate,
                        quantity: i.quantity,
                        variantId: i.variantId,
                        variantName: i.VariantName,
                        addonId: i.addonId,
                        addonName: i.addonName
                    })),
                    totalAmount: totals.totalAmount,
                    discount: b.discount,
                    otherCharges: b.otherCharges,
                    netAmount: totals.netAmount,
                    roundOff: totals.roundOff,
                    cgst: totals.cgst,
                    sgst: totals.sgst,
                    totalTax: totals.totalTax,
                    paymentDetails: details,
                    creditAccount: b.paymentMethod === 'due' ? 'Customer Credit' : ' '
                };
            });

            const res = await axios.post(`${apiBaseUrl}/splitBill/${logginedDB}`, {
                originalBillNo,
                tableno,
                area_id: areaId,
                tableType,
                userTypeInt: userType,
                splits: payloadSplits
            });

            if (res.data.success) {
                alert("Bills split and settled successfully!");
                onSuccess(res.data.billNos);
                onClose();
            } else {
                alert("Failed to settle split bill: " + res.data.message);
            }
        } catch (error) {
            console.error(error);
            alert("Error settling split bill.");
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 bg-black/60 backdrop-blur-sm overflow-y-auto font-nunito [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="relative w-full max-w-6xl h-[86vh] max-h-[86vh] bg-white border border-[#E8E8E8] rounded-xl shadow-2xl overflow-hidden flex flex-col">
                
                {/* Header */}
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E8E8E8] bg-white shrink-0">
                    <div className="min-w-0">
                        <h4 className="text-lg font-bold text-[#0008B4] leading-tight">
                            Split Bill - Table {tableno}
                        </h4>
                        <p className="text-xs text-[#666666] m-0">
                            Original Bill No: #{originalBillNo} <span className="text-[#C6C6C6]">|</span> Total Amount: ₹{originalTotalAmt.toFixed(2)}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <div className="flex items-center bg-[#F7F7F7] border border-[#E8E8E8] rounded-lg p-1 gap-1">
                            <button
                                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
                                    splitMode === 'items'
                                        ? 'bg-[#0008B4] text-white shadow-sm'
                                        : 'text-[#464255] hover:text-[#0008B4] hover:bg-white'
                                }`}
                                onClick={() => setSplitMode('items')}
                            >
                                Items
                            </button>
                            <button
                                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
                                    splitMode === 'equal'
                                        ? 'bg-[#0008B4] text-white shadow-sm'
                                        : 'text-[#464255] hover:text-[#0008B4] hover:bg-white'
                                }`}
                                onClick={() => setSplitMode('equal')}
                            >
                                Equal
                            </button>
                        </div>
                        <button
                            type="button"
                            className="text-[#666666] hover:text-[#0008B4] transition-colors duration-150 p-2 rounded-lg hover:bg-[#F5F5F5]"
                            onClick={onClose}
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="p-3 bg-[#F5F5F5] flex-1 min-h-0 overflow-hidden">
                    {splitMode === 'equal' ? (
                        /* EQUAL SPLIT VIEW */
                        <div className="h-full overflow-y-auto flex flex-col gap-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                            <div className="flex flex-col md:flex-row items-center gap-3 p-3 bg-white border border-[#E8E8E8] rounded-lg shadow-sm">
                                <div className="w-full md:w-1/3">
                                    <label className="block text-[11px] font-semibold text-[#666666] uppercase tracking-wider mb-1">Number of Splits</label>
                                    <select 
                                        className="w-full bg-white border border-[#D8D8D8] rounded-lg px-3 py-1.5 text-sm text-[#464255] font-semibold focus:outline-none focus:border-[#0008B4] focus:ring-2 focus:ring-[#0008B4]/10"
                                        value={numEqualSplits}
                                        onChange={(e) => setNumEqualSplits(parseInt(e.target.value))}
                                    >
                                        {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                            <option key={n} value={n}>{n} Guest Shares</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="w-full md:w-2/3 flex flex-col items-end justify-center">
                                    <div className="px-4 py-2 rounded-lg bg-[#0008B4]/5 border border-[#0008B4]/15 text-right">
                                        <span className="text-xs font-semibold text-[#666666]">Share Amount per Guest:</span>
                                        <h3 className="text-xl font-bold text-[#0008B4] leading-tight mt-0.5">₹{calculateEqualSplitTotals().shareNetAmount}</h3>
                                        <p className="text-[10px] text-[#888888] mt-0.5">Round Off: ₹{calculateEqualSplitTotals().shareRoundOff}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {equalSplits.map((eq, index) => (
                                    <div className="bg-white border border-[#E8E8E8] rounded-lg overflow-hidden hover:border-[#0008B4]/30 hover:shadow-sm transition-all duration-200" key={eq.id}>
                                        <div className="bg-[#F7F7F7] px-3 py-2 border-b border-[#E8E8E8] flex justify-between items-center">
                                            <span className="font-semibold text-sm text-[#0008B4]">Split Share #{eq.id}</span>
                                            <span className="text-xs font-bold text-[#0008B4] bg-[#0008B4]/10 px-2 py-1 rounded">₹{calculateEqualSplitTotals().shareNetAmount}</span>
                                        </div>
                                        <div className="p-3 flex flex-col gap-2">
                                            <div>
                                                <label className="block text-[11px] font-semibold text-[#666666] uppercase tracking-wider mb-1">Guest Name</label>
                                                <input 
                                                    type="text" 
                                                    className="w-full bg-white border border-[#D8D8D8] rounded-lg px-3 py-1 text-sm text-[#464255] focus:outline-none focus:border-[#0008B4] focus:ring-2 focus:ring-[#0008B4]/10"
                                                    value={eq.customerName}
                                                    onChange={(e) => {
                                                        const newSplits = [...equalSplits];
                                                        newSplits[index].customerName = e.target.value;
                                                        setEqualSplits(newSplits);
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-semibold text-[#666666] uppercase tracking-wider mb-1">Payment Mode</label>
                                                <select 
                                                    className="w-full bg-white border border-[#D8D8D8] rounded-lg px-3 py-1 text-sm text-[#464255] focus:outline-none focus:border-[#0008B4] focus:ring-2 focus:ring-[#0008B4]/10"
                                                    value={eq.paymentMethod}
                                                    onChange={(e) => {
                                                        const newSplits = [...equalSplits];
                                                        newSplits[index].paymentMethod = e.target.value;
                                                        setEqualSplits(newSplits);
                                                    }}
                                                >
                                                    <option value="cash">Cash</option>
                                                    <option value="card">Card</option>
                                                    <option value="online">Online</option>
                                                    <option value="due">Credit / Due</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        /* ITEMIZED SPLIT VIEW */
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 h-full min-h-0">

                            {/* Left Column: Unallocated Items */}
                            <div className="lg:col-span-4 h-full min-h-0 bg-white border border-[#E8E8E8] rounded-lg overflow-hidden flex flex-col shadow-sm">
                                <div className="bg-[#F7F7F7] px-3 py-2 border-b border-[#E8E8E8] flex justify-between items-center">
                                    <span className="font-semibold text-[#464255]">Unallocated Items</span>
                                    <span className="text-xs font-bold bg-[#FF5B5B]/10 text-[#D92D20] border border-[#FF5B5B]/20 px-2 py-0.5 rounded-full">
                                        {unallocatedItems.reduce((acc, i) => acc + i.unallocatedQty, 0)} items left
                                    </span>
                                </div>
                                <div className="p-2 flex-1 min-h-0 overflow-y-auto flex flex-col gap-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                                    {unallocatedItems.length === 0 ? (
                                        <p className="text-[#888888] text-center py-8 text-sm">No items to split</p>
                                    ) : (
                                        unallocatedItems.map((item, idx) => (
                                            item.unallocatedQty > 0 && (
                                                <div className="p-2 rounded-lg bg-white border border-[#E8E8E8] hover:border-[#0008B4]/30 hover:shadow-sm transition-all duration-150 flex flex-col gap-1.5" key={idx}>
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1 pr-2">
                                                            <span className="font-semibold text-[#464255] text-sm block truncate" title={item.ItemName}>
                                                                {item.ItemName}
                                                            </span>
                                                            {item.VariantName && <span className="text-xs text-[#B7791F] mt-0.5 block">{item.VariantName}</span>}
                                                            {item.addonName && <span className="text-[11px] text-[#0008B4] mt-0.5 block">Addon: {item.addonName}</span>}
                                                        </div>
                                                        <div className="text-right">
                                                            <span className="text-xs font-semibold text-[#464255] block">₹{item.Rate}</span>
                                                            <span className="text-[11px] text-[#888888] block">Qty: {item.unallocatedQty}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1 mt-0.5 border-t border-[#E8E8E8] pt-1.5 justify-end">
                                                        {splitBills.map(bill => (
                                                            <button
                                                                key={bill.id}
                                                                className="px-2 py-0.5 text-[10px] font-bold border border-[#0008B4]/25 text-[#0008B4] bg-[#0008B4]/5 hover:bg-[#0008B4] hover:text-white rounded transition-colors duration-150"
                                                                onClick={() => moveItemToBill(idx, bill.id)}
                                                            >
                                                                + Bill {bill.id}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Right Column: Split Bills Grid */}
                            <div className="lg:col-span-8 h-full min-h-0 overflow-y-auto pr-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {splitBills.map(bill => {
                                        const totals = calculateTotals(bill.items, bill.discount, bill.otherCharges);
                                        return (
                                            <div className="bg-white border border-[#E8E8E8] rounded-lg overflow-hidden flex flex-col justify-between shadow-sm" key={bill.id}>

                                                {/* Header */}
                                                <div className="bg-[#F7F7F7] px-3 py-2 border-b border-[#E8E8E8] flex justify-between items-center">
                                                    <span className="font-semibold text-[#0008B4] text-sm">Split Bill #{bill.id}</span>
                                                    {splitBills.length > 2 && (
                                                        <button
                                                            className="text-xs text-[#D92D20] hover:text-[#B42318] font-medium px-2 py-0.5 border border-[#FF5B5B]/25 rounded hover:bg-[#FF5B5B]/10 transition-colors"
                                                            onClick={() => removeSplitBill(bill.id)}
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Allocated items list */}
                                                <div className="p-2 flex-1 flex flex-col gap-1.5 max-h-[170px] overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                                                    {bill.items.length === 0 ? (
                                                        <div className="flex-1 flex items-center justify-center py-8">
                                                            <p className="text-xs text-[#888888] text-center">Add items from the unallocated list</p>
                                                        </div>
                                                    ) : (
                                                        bill.items.map((item, itemIdx) => (
                                                            <div className="flex justify-between items-center p-2 rounded-lg bg-[#F9F9F9] border border-[#E8E8E8]" key={itemIdx}>
                                                                <div className="flex-1 min-w-0 pr-2">
                                                                    <span className="text-xs font-semibold text-[#464255] block truncate">{item.ItemName}</span>
                                                                    {item.VariantName && <span className="text-[10px] text-[#B7791F]">{item.VariantName}</span>}
                                                                    <span className="text-[10px] text-[#888888] block">₹{item.rate} x {item.quantity}</span>
                                                                </div>
                                                                <button
                                                                    className="w-6 h-6 flex items-center justify-center border border-[#FF5B5B]/30 text-[#D92D20] hover:bg-[#D92D20] hover:text-white rounded-md transition-colors"
                                                                    onClick={() => removeItemFromBill(bill.id, itemIdx)}
                                                                >
                                                                    -
                                                                </button>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>

                                                {/* Bill config inputs */}
                                                <div className="p-2 border-t border-[#E8E8E8] bg-[#FAFAFA] mt-auto">
                                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                                        <div>
                                                            <input
                                                                type="text"
                                                                className="w-full bg-white border border-[#D8D8D8] rounded-lg px-2.5 py-0.5 text-xs text-[#464255] focus:outline-none focus:border-[#0008B4] focus:ring-2 focus:ring-[#0008B4]/10"
                                                                placeholder="Guest Name"
                                                                value={bill.customerName}
                                                                onChange={(e) => {
                                                                    const updated = splitBills.map(b => b.id === bill.id ? { ...b, customerName: e.target.value } : b);
                                                                    setSplitBills(updated);
                                                                }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <select
                                                                className="w-full bg-white border border-[#D8D8D8] rounded-lg px-2 py-0.5 text-xs text-[#464255] focus:outline-none focus:border-[#0008B4] focus:ring-2 focus:ring-[#0008B4]/10"
                                                                value={bill.paymentMethod}
                                                                onChange={(e) => {
                                                                    const updated = splitBills.map(b => b.id === bill.id ? { ...b, paymentMethod: e.target.value } : b);
                                                                    setSplitBills(updated);
                                                                }}
                                                            >
                                                                <option value="cash">Cash</option>
                                                                <option value="card">Card</option>
                                                                <option value="online">Online</option>
                                                                <option value="due">Credit / Due</option>
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                                        <div className="flex items-center bg-white border border-[#D8D8D8] rounded-lg px-2 py-0.5 focus-within:border-[#0008B4] focus-within:ring-2 focus-within:ring-[#0008B4]/10">
                                                            <span className="text-[10px] text-[#888888] font-semibold uppercase pr-1.5">Disc%</span>
                                                            <input
                                                                type="number"
                                                                className="w-full bg-transparent border-0 text-xs text-[#464255] text-right focus:outline-none focus:ring-0 p-0"
                                                                value={bill.discount}
                                                                min="0"
                                                                max="100"
                                                                onChange={(e) => {
                                                                    const val = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                                                    const updated = splitBills.map(b => b.id === bill.id ? { ...b, discount: val } : b);
                                                                    setSplitBills(updated);
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="flex items-center bg-white border border-[#D8D8D8] rounded-lg px-2 py-0.5 focus-within:border-[#0008B4] focus-within:ring-2 focus-within:ring-[#0008B4]/10">
                                                            <span className="text-[10px] text-[#888888] font-semibold uppercase pr-1.5">Charges</span>
                                                            <input
                                                                type="number"
                                                                className="w-full bg-transparent border-0 text-xs text-[#464255] text-right focus:outline-none focus:ring-0 p-0"
                                                                value={bill.otherCharges}
                                                                min="0"
                                                                onChange={(e) => {
                                                                    const val = Math.max(0, parseFloat(e.target.value) || 0);
                                                                    const updated = splitBills.map(b => b.id === bill.id ? { ...b, otherCharges: val } : b);
                                                                    setSplitBills(updated);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="flex justify-between items-center px-2 py-1.5 rounded-lg bg-[#2e7d32]/10 border border-[#2e7d32]/15">
                                                        <span className="text-[11px] font-medium text-[#666666]">Total Net Amount:</span>
                                                        <span className="text-sm font-bold text-[#2e7d32]">₹{totals.netAmount}</span>
                                                    </div>
                                                </div>

                                            </div>
                                        );
                                    })}
                                    <div className="bg-white border-2 border-dashed border-[#C6C6C6] hover:border-[#0008B4]/40 rounded-lg flex items-center justify-center p-4 cursor-pointer hover:bg-[#0008B4]/5 transition-all duration-200 group min-h-[110px]" onClick={addSplitBill}>
                                        <div className="text-center">
                                            <span className="text-[#888888] group-hover:text-[#0008B4] transition-colors block text-2xl font-bold mb-1">+</span>
                                            <span className="text-xs font-semibold text-[#888888] group-hover:text-[#0008B4] transition-colors uppercase tracking-wider">Add Another Bill</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    )}

                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#E8E8E8] bg-white shrink-0">
                    <button
                        type="button"
                        className="px-4 py-1.5 border border-[#D8D8D8] bg-white hover:bg-[#F5F5F5] rounded-lg text-sm text-[#464255] hover:text-[#0008B4] transition-colors duration-150"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <div className="flex items-center gap-3">
                        {splitMode === 'items' && unallocatedItems.some(i => i.unallocatedQty > 0) && (
                            <span className="text-xs font-medium text-[#B7791F] animate-pulse">Allocate all items to settle split bill</span>
                        )}
                        <button
                            type="button"
                            className="px-5 py-1.5 bg-[#0008B4] hover:bg-[#00069A] text-white font-bold rounded-lg shadow-sm transition-all duration-200 text-sm"
                            onClick={splitMode === 'equal' ? handleSettleEqualSplit : handleSettleItemizedSplit}
                        >
                            Confirm & Settle Split
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default SplitBillModal;
